import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';

// Dashboard Stats Skeleton - Matches MainDashboard card structure
export function DashboardStatsSkeleton() {
  const colors = [
    {
      bg: 'from-white to-blue-50/30 dark:from-gray-900 dark:to-blue-950/20',
      circle: 'bg-blue-500/5 dark:bg-blue-500/10',
    },
    {
      bg: 'from-white to-yellow-50/30 dark:from-gray-900 dark:to-amber-950/20',
      circle: 'bg-yellow-500/5 dark:bg-amber-500/10',
    },
    {
      bg: 'from-white to-green-50/30 dark:from-gray-900 dark:to-emerald-950/20',
      circle: 'bg-green-500/5 dark:bg-emerald-500/10',
    },
    {
      bg: 'from-white to-blue-50/30 dark:from-gray-900 dark:to-blue-950/20',
      circle: 'bg-blue-500/5 dark:bg-blue-500/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      {colors.map((color, i) => (
        <Card
          key={i}
          className={`relative overflow-hidden border-gray-200 bg-gradient-to-br dark:border-gray-800 ${color.bg}`}
        >
          <div className="relative z-10 p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="relative">
                <Skeleton className="h-12 w-12 rounded-xl" />
              </div>
              <Skeleton className="h-5 w-14 rounded-md" />
            </div>
            <div>
              <Skeleton className="mb-2 h-4 w-32" />
              <Skeleton className="mb-1 h-7 w-20" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
          {/* Decorative circle */}
          <div
            className={`absolute bottom-0 right-0 h-32 w-32 ${color.circle} -mb-16 -mr-16 rounded-full`}
          />
        </Card>
      ))}
    </div>
  );
}

// Doctors Grid Skeleton
export function DoctorsGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card
          key={i}
          className="border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900"
        >
          <div className="mb-4 flex items-start space-x-4">
            <Skeleton className="h-16 w-16 flex-shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
          <div className="mb-4 space-y-3">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 flex-1" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 flex-1" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 flex-1 rounded-lg" />
            <Skeleton className="h-9 w-9 rounded-lg" />
          </div>
        </Card>
      ))}
    </div>
  );
}

// Doctor Profile Header Skeleton - Matches simplified header design
export function DoctorProfileHeaderSkeleton() {
  return (
    <Card className="border-gray-200 bg-gradient-to-br from-white via-white to-blue-50/30 shadow-md dark:border-gray-800 dark:from-gray-900 dark:via-gray-900 dark:to-blue-950/20">
      <div className="p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          {/* Left: Avatar + Info */}
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-start">
            {/* Avatar with ring */}
            <Skeleton className="h-24 w-24 rounded-full" />

            <div className="flex-1 space-y-4">
              {/* Name + Badge */}
              <div className="flex flex-wrap items-center gap-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>

              {/* Specialty */}
              <Skeleton className="h-5 w-32" />

              {/* Contact info (phone, email, address) */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-9 rounded-lg" />
                  <Skeleton className="h-4 w-28" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-9 rounded-lg" />
                  <Skeleton className="h-4 w-40" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-9 rounded-lg" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: WhatsApp Button */}
          <div className="w-full sm:w-auto">
            <Skeleton className="h-11 w-full rounded-lg sm:w-56" />
          </div>
        </div>
      </div>
    </Card>
  );
}

// Table Skeleton (for Documents, Invoices, Payments)
export function TableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <Card className="border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              {[1, 2, 3, 4, 5].map((i) => (
                <th key={i} className="px-6 py-4">
                  <Skeleton className="h-4 w-24" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {Array.from({ length: rows }).map((_, i) => (
              <tr key={i}>
                {[1, 2, 3, 4, 5].map((j) => (
                  <td key={j} className="px-6 py-4">
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Reports Chart Skeleton
export function ReportsChartSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card
            key={i}
            className="border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="mb-4 flex items-center justify-between">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <Skeleton className="mb-2 h-8 w-32" />
            <Skeleton className="h-4 w-24" />
          </Card>
        ))}
      </div>
      <Card className="border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="mb-6">
          <Skeleton className="mb-2 h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-80 w-full rounded-lg" />
      </Card>
    </div>
  );
}

// Settings Form Skeleton
export function SettingsFormSkeleton() {
  return (
    <div className="space-y-6">
      {/* Tabs Skeleton */}
      <div className="overflow-x-auto">
        <div className="inline-flex min-w-full gap-2 rounded-xl border-0 bg-gray-100 p-1 shadow-sm dark:bg-gray-800 sm:min-w-0">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-32 rounded-lg" />
          ))}
        </div>
      </div>

      {/* General Tab Skeleton */}
      <Card className="border-gray-200 bg-white p-8 shadow-lg dark:border-gray-800 dark:bg-gray-900">
        <div className="mb-8 flex items-center space-x-3">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="space-y-6">
          {/* Grid 3 columnas para General */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
            ))}
          </div>
          {/* Info adicional */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-800">
            <Skeleton className="h-20 w-full rounded-xl" />
          </div>
          <div className="flex justify-end pt-4">
            <Skeleton className="h-11 w-40 rounded-lg" />
          </div>
        </div>
      </Card>
    </div>
  );
}

// Tab Content Skeleton (for Doctor Profile tabs)
export function TabContentSkeleton({ type = 'list' }: { type?: 'list' | 'table' | 'form' }) {
  if (type === 'table') {
    return <TableSkeleton rows={5} />;
  }

  if (type === 'form') {
    return (
      <Card className="border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <Card
          key={i}
          className="border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900"
        >
          <div className="flex items-center justify-between">
            <div className="flex flex-1 items-center space-x-4">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
        </Card>
      ))}
    </div>
  );
}

// Recent Activity Skeleton (for Dashboard)
export function RecentActivitySkeleton() {
  return (
    <Card className="border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
      <div className="mb-6 flex items-center justify-between">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-9 w-24 rounded-lg" />
      </div>
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex items-start space-x-4 border-b border-gray-200 pb-4 last:border-0 last:pb-0 dark:border-gray-800"
          >
            <Skeleton className="h-12 w-12 flex-shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-9 w-20 rounded-lg" />
          </div>
        ))}
      </div>
    </Card>
  );
}

// Accordion Sections Skeleton - For Doctor Profile with collapsible sections
export function AccordionSectionsSkeleton() {
  return (
    <div className="space-y-4">
      {/* 6 accordion sections matching the real component */}
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card
          key={i}
          className="border-gray-200 bg-white transition-all duration-200 dark:border-gray-800 dark:bg-gray-900"
        >
          {/* Accordion Header */}
          <div className="flex cursor-pointer items-center justify-between px-6 py-5 hover:bg-gray-50 dark:hover:bg-gray-800/50">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <div className="space-y-2.5">
                <Skeleton className="h-5 w-48 sm:w-56" />
                <Skeleton className="h-4 w-56 sm:w-64" />
              </div>
            </div>
            <Skeleton className="h-6 w-6 rounded" />
          </div>
        </Card>
      ))}
    </div>
  );
}

// Page Container Skeleton (full page)
export function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    </div>
  );
}
